import { faFilePen, faMemo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import { useCallback, useState } from 'react'
import { useApplicantReports } from '~/api/queries/applicant'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useConfig } from '~/domains/configuration/queries/config'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { themeClass } from '~/styles/theme.css'
import {
  applicantModalAsideSection,
  applicantModalAsideSectionTitle,
  applicantModalAsideSectionTitleWithButton
} from '../ApplicantModal.css'
import {
  reportsListingButtons,
  reportsListingItem,
  reportsListingItemShimmer,
  reportsListingItemTitleWrapper,
  reportsListingList,
  reportsListingTitleLegacy
} from './ApplicantReports.css'
import { ApplicantReportItem } from './ApplicantReportsItem'

const ReportShimmer = () => {
  return (
    <div className={clsx(reportsListingItem, reportsListingItemShimmer)}>
      <div>
        <Shimmer width="32px" height="32px" />
      </div>
      <div className={reportsListingItemTitleWrapper}>
        <Shimmer width="90%" height="20px" />
        <Shimmer width="100px" height="16px" />
      </div>
    </div>
  )
}

export const ApplicantReports = ({
  applicantId,
  legacyStyle
}: {
  applicantId: number
  legacyStyle?: boolean
}) => {
  const [currentEdit, setCurrentEdit] = useState<'new' | number | null>(null)
  const [lastCreatedId, setLastCreatedId] = useState<number | null>(null)
  const [launchGeneration, setLaunchGeneration] = useState<boolean>(false)

  useConfig('interview_report.sections')
  const {
    applicantReports,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = useApplicantReports({
    applicantId
  })

  const endOfListReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached
  })

  return (
    <div className={clsx(applicantModalAsideSection, themeClass)}>
      <div
        className={clsx(
          applicantModalAsideSectionTitle,
          applicantModalAsideSectionTitleWithButton
        )}
      >
        <div className={clsx(legacyStyle ? reportsListingTitleLegacy : null)}>
          <FontAwesomeIcon icon={faMemo} /> Comptes rendus d'entretien
        </div>
        {((applicantReports && applicantReports?.length > 0) ||
          currentEdit !== null) && (
          <div className={clsx(reportsListingButtons, themeClass)}>
            {currentEdit !== 'new' && (
              <Button
                variation={ButtonVariation.Neutral}
                buttonSize="extraSmall"
                onClick={() => {
                  setLaunchGeneration(false)
                  setCurrentEdit('new')
                  setLastCreatedId(null)
                }}
              >
                <FontAwesomeIcon icon={faFilePen} /> Nouveau
              </Button>
            )}
          </div>
        )}
      </div>
      <motion.div
        key="list"
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
        }}
        initial="hidden"
        animate="visible"
        className={reportsListingList}
      >
        {currentEdit === 'new' && (
          <ApplicantReportItem
            key={currentEdit}
            showEdit={true}
            onChangeCurrentEdit={(newEdit: 'new' | null | number) => {
              if (typeof newEdit === 'number') {
                // In case of new report creation, catch new id to avoid duplicates in the list keys
                setLastCreatedId(newEdit)
              } else {
                setCurrentEdit(newEdit)
                setLastCreatedId(null)
              }
            }}
            applicantId={applicantId}
            launchGeneration={launchGeneration === true}
          />
        )}
        {isError && (
          <div key="error">
            <Alert
              variation={AlertVariation.Danger}
              message="Une erreur est survenue lors de la récupération des comptes rendus"
              onDismiss={false}
            />
          </div>
        )}
        {applicantReports
          ?.filter((report) => report.id !== lastCreatedId && report.createdBy)
          .map((report) => (
            <ApplicantReportItem
              key={report.id}
              data={report}
              showEdit={currentEdit === report.id}
              onChangeCurrentEdit={setCurrentEdit}
              applicantId={applicantId}
            />
          ))}
        {applicantReports?.length === 0 && currentEdit !== 'new' && (
          <Button
            key="new-report"
            variation={ButtonVariation.Neutral}
            onClick={() => {
              setLaunchGeneration(false)
              setCurrentEdit('new')
              setLastCreatedId(null)
            }}
            buttonSize="small"
            fullWidth={true}
          >
            <FontAwesomeIcon icon={faFilePen} />
            Nouveau compte-rendu
          </Button>
        )}
        {isLoading && (
          <>
            <ReportShimmer />
            <ReportShimmer />
            <ReportShimmer />
          </>
        )}
      </motion.div>
      <div ref={lastItemRef} />
    </div>
  )
}
